<template>
  <div class="monitoring-curve">
    <div class="header-section">
      <div class="header-row">
        <div class="title-section">
          <img src="@/assets/gisWork/SVG/title-bg.svg" class="title-bg" />
          <span class="title-text">监测曲线</span>
        </div>
      </div>

      <!-- 选择区域 -->
      <div class="selection-row">
        <a-tree-select :tree-data="zoneTreeData" v-model="zoneName" style="width: 200px;"
          :replace-fields="{ title: 'zoneName', key: 'id', value: 'id', selectable: 'selectable' }"
          :dropdown-style="{ maxHeight: '400px', overflow: 'auto' }" placeholder="请选择片区" allow-clear
          tree-default-expand-all @change="onZoneChange" />
        <a-button type="primary" @click="handleQuery" style="margin-left: 30px;">查询</a-button>
        <a-button @click="handleReset" style="margin-left: 10px;">重置</a-button>
      </div>
    </div>

    <!-- 内容区域 -->
    <div class="content-area">
      <div v-if="loading" class="loading-wrapper">
        <a-spin size="large">
          <div class="loading-content">
            <div class="loading-text">数据加载中...</div>
          </div>
        </a-spin>
      </div>
      <div v-else class="dataWrap">
        <div class="dataItem" v-for="(item, index) in dataList" :key="index">
          <div class="dataItem-top">
            <div class="topTitle" @click="goAnalysis(item.zoneName)" :title="item.zoneName">{{ item.zoneName }}</div>
            <div class="t-gc-box">高程：{{ item.groundElevation }}</div>
          </div>
          <div class="chart-wrapper" :ref="`chart-${item.id}`" v-if="item.hasData !== false"></div>
          <div class="no-data-wrapper" v-else>
            <a-empty description="暂无数据" />
          </div>
        </div>
      </div>
    </div>
    <analysis-modal ref="AnalysisModal"></analysis-modal>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { getUUID } from '@/utils/util'
import AnalysisModal from './AnalysisModal'

export default {
  name: 'MonitoringCurve',
  components: {
    AnalysisModal
  },
  data () {
    return {
      zoneName: null,
      zoneId: null,
      zoneTreeData: [], // 片区选项
      queryZoneDateUrl: 'zone-metering/zone-metering',
      loading: false,
      len: 0,
      row: 3,
      dataList: [],
      chartInstances: [] // 存储图表实例
    }
  },
  created () {
    this.getZoneData()
    this.getData()
  },
  mounted () {
    // 监听窗口大小变化
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy () {
    // 移除窗口监听
    window.removeEventListener('resize', this.handleResize)
    // 销毁所有图表实例
    this.chartInstances.forEach(chart => {
      if (chart) {
        chart.dispose()
      }
    })
  },
  methods: {
    getZoneData () {
      this.$http.get(this.queryZoneDateUrl).then(({ data }) => {
        this.zoneTreeData = data
      })
    },
    handleQuery () {
      this.getData()
    },
    handleReset () {
      this.zoneName = null
      this.zoneId = null
      this.getData()
    },
    onZoneChange (value, label, extra) {
      this.zoneId = value
      // 从 extra 中获取片区名称
      if (extra && extra.triggerNode) {
        this.zoneName = extra.triggerNode.title
      }
    },
    goAnalysis (name) {
      this.$refs.AnalysisModal.edit(name)
    },
    getData () {
      this.loading = true
      const params = {
        zoneId: this.zoneId
      }
      this.$http
        .get('/pipe-network/piezometer/analysis/statement', { params })
        .then(({ data }) => {
          const list = data
          list.map((item) => {
            item.id = getUUID()
            const statementData = item.statementData
            const xData = []
            const series = []

            // 检查是否有数据
            if (!statementData || statementData.length === 0) {
              item.hasData = false
              return
            }

            // 计算压力最值（勿固定 yAxis.interval 为小步长，否则最大值大时会产生数百个刻度导致左侧标签重叠成「涂抹」）
            let maxPressure = -Infinity
            let minPressure = Infinity
            for (let i = 0; i < statementData.length; i++) {
              for (let j = 0; j < statementData[i].length; j++) {
                const p = Number(statementData[i][j].pressure)
                if (!Number.isFinite(p)) continue
                if (p > maxPressure) maxPressure = p
                if (p < minPressure) minPressure = p
              }
            }
            if (!Number.isFinite(maxPressure)) maxPressure = 0
            if (!Number.isFinite(minPressure)) minPressure = 0
            const range = Math.max(maxPressure - minPressure, 0)
            const bottomPad = Math.max(range * 0.08, 0.05)
            // 顶部多留一档：最大值 markPoint（三角/气泡+文字）会高出曲线，yMax 贴太紧会被绘图区上沿裁切
            const topPad = Math.max(range * 0.18, 0.4, maxPressure * 0.015)
            const yMax = maxPressure + topPad
            const yMin = Math.max(0, minPressure - bottomPad)

            for (let i = 0; i < statementData.length; i++) {
              const sdata = []
              for (let j = 0; j < statementData[i].length; j++) {
                sdata.push(statementData[i][j].pressure)
                if (i == 0) {
                  xData.push(statementData[i][j].time)
                }
              }
              const seriesData = {
                name: statementData[i][0].piezometerNo,
                data: sdata,
                type: 'line',
                smooth: false,
                showSymbol: false,
                lineStyle: {
                  width: 2,
                  color: '#088cf6'
                },
                markPoint: {
                  data: [
                    { type: 'max', name: '最大值' },
                    { type: 'min', name: '最小值' }
                  ]
                },
                // 避免极值 markPoint 被直角坐标系裁切（配合 y 轴顶部留白）
                clip: false
              }
              series.push(seriesData)
            }
            item.option = {
              backgroundColor: '#ffffff',
              color: ['#088cf6'],
              xAxis: {
                type: 'category',
                data: xData,
                axisTick: {
                  show: false
                },
                axisLine: {
                  show: false
                },
                splitLine: {
                  show: true,
                  lineStyle: {
                    color: '#e8e8e8',
                    type: 'solid'
                  }
                }
              },
              yAxis: {
                type: 'value',
                name: 'Mpa',
                nameLocation: 'end',
                nameGap: 12,
                nameTextStyle: {
                  color: '#999',
                  fontSize: 12
                },
                min: yMin,
                max: yMax,
                splitNumber: 5,
                axisTick: {
                  show: false
                },
                axisLine: {
                  show: false
                },
                axisLabel: {
                  margin: 12,
                  hideOverlap: true,
                  formatter: function (value) {
                    const v = Number(value)
                    if (!Number.isFinite(v)) return ''
                    const abs = Math.abs(v)
                    if (abs >= 100) return v.toFixed(0)
                    if (abs >= 10) return v.toFixed(1)
                    return v.toFixed(2)
                  }
                },
                splitLine: {
                  show: true,
                  lineStyle: {
                    color: '#e8e8e8',
                    type: 'solid'
                  }
                }
              },
              tooltip: {
                trigger: 'axis'
              },
              grid: {
                left: '5%',
                right: '5%',
                top: '14%',
                bottom: '5%',
                containLabel: true
              },
              series: series
            }
          })
          this.dataList = list
          this.len = this.dataList.length
          this.loading = false

          // 等待DOM更新后初始化图表
          this.$nextTick(() => {
            this.initCharts()
          })
        })
        .catch(() => {
          this.loading = false
        })
    },
    initCharts () {
      // 先销毁旧的图表实例
      this.chartInstances.forEach(chart => {
        if (chart) {
          chart.dispose()
        }
      })
      this.chartInstances = []

      // 为每个数据项初始化图表
      this.dataList.forEach(item => {
        const chartDom = this.$refs[`chart-${item.id}`]
        if (chartDom && chartDom[0]) {
          const chart = echarts.init(chartDom[0])
          chart.setOption(item.option)
          this.chartInstances.push(chart)
        }
      })
    },
    handleResize () {
      // 窗口大小变化时调整所有图表
      this.chartInstances.forEach(chart => {
        if (chart) {
          chart.resize()
        }
      })
    }
  }
}
</script>

<style lang="less" scoped>
.monitoring-curve {
  width: 100%;
  padding: 10px;
  padding-bottom: 20px;
}

.header-section {
  background-color: white;
  border-radius: 10px;
  padding: 10px;
  padding-bottom: 20px;
}

.header-row {
  display: flex;
  align-items: center;
}

.title-section {
  position: relative;
  display: inline-block;

  .title-bg {
    width: 250px;
    height: 50px;
    object-fit: contain;
  }

  .title-text {
    position: absolute;
    top: 47%;
    left: 27%;
    transform: translate(-50%, -50%);
    color: #093d97;
    font-weight: 600;
    font-size: 14px;
    white-space: nowrap;
    z-index: 2;
  }
}

.selection-row {
  display: flex;
  align-items: center;
  margin-top: 15px;
  margin-left: 10px;
}

.content-area {
  height: 680px;
  margin-top: 20px;
  padding: 20px;
  background-color: white;
  border-radius: 10px;
  overflow-y: auto;
  position: relative;

  /* 自定义滚动条样式 */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;

    &:hover {
      background: #a8a8a8;
    }
  }
}

.dataWrap {
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  flex-wrap: wrap;

  .dataItem {
    border-width: 0px;
    left: 0px;
    top: 0px;
    width: 500px;
    height: 320px;
    background: inherit;
    background-color: rgba(255, 255, 255, 1);
    border: none;
    border-radius: 10px;
    box-shadow: 0px 0px 10px rgba(151, 151, 151, 0.349019607843137);
    margin-bottom: 24px;

    .dataItem-top {
      display: flex;
      justify-content: center;
      align-items: center;
      flex-direction: row;
      padding: 16px;
      min-height: 40px;
      line-height: 22px;
      position: relative;

      .t-gc-box {
        position: absolute;
        right: 16px;
        font-size: 12px;
        font-weight: 400;
        white-space: nowrap;
      }

      .topTitle {
        font-family: MicrosoftYaHeiSemibold;
        font-size: 16px;
        color: #101010;
        letter-spacing: 0.63px;
        font-weight: 600;
        cursor: pointer;
        display: block;
        max-width: calc(100% - 150px);
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        text-align: center;
      }

      .topTitle:hover {
        color: #0265ff;
      }
    }

    .chart-wrapper {
      width: 100%;
      height: calc(100% - 54px);
      padding: 0px 5px 5px 5px;
      // margin-top: 5px;
    }

    .no-data-wrapper {
      width: 100%;
      height: calc(100% - 54px);
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  .list {
    content: '';
    width: 32%;
  }
}

.loading-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  left: 0;
  background-color: rgba(255, 255, 255, 0.9);
  z-index: 10;
}

.loading-content {
  text-align: center;
}

.loading-text {
  margin-top: 64px;
  color: #666;
  font-size: 14px;
}
</style>
